import { useCallback, useEffect, useRef, useState } from 'react';
import { getCities, getCityById } from '../../../../apis';
import { CountriesAutocomplete } from '../AllowedandForbiddenCountries/Countries';

type Field = 'from' | 'to';

const MIN_CITY_SEARCH_CHARS = 2;
const SEARCH_CITIES_LIMIT = 20;

interface RelationCodes {
  from: string;
  to: string;
}

export const useCityRelations = (codes: RelationCodes, enabled: boolean) => {
  const [options, setOptions] = useState<Record<Field, CountriesAutocomplete>>({
    from: [],
    to: [],
  });

  const [labels, setLabels] = useState<Record<string, string>>({});
  const requestSeq = useRef<Record<Field, number>>({ from: 0, to: 0 });

  const cacheLabels = useCallback((items: CountriesAutocomplete) => {
    setLabels(prev => {
      const next = { ...prev };
      items.forEach(item => {
        if (item.subtitle) next[item.subtitle] = item.title;
      });
      return next;
    });
  }, []);

  const cacheLabel = useCallback((code: string, name: string) => {
    setLabels(prev => ({ ...prev, [code]: name }));
  }, []);

  const resolveLabel = useCallback((code: string) => labels[code] ?? '', [labels]);

  const clearOptions = useCallback((field: Field) => {
    setOptions(prev => ({ ...prev, [field]: [] }));
  }, []);

  const runQuery = useCallback(
    (query: string, field: Field) => {
      const seq = (requestSeq.current[field] += 1);

      getCities(query, SEARCH_CITIES_LIMIT)
        .then(cities => {
          if (seq !== requestSeq.current[field]) return;

          const items: CountriesAutocomplete = cities.map(city => ({
            title: city.name,
            subtitle: city.id,
            key: city.id,
          }));
          cacheLabels(items);
          setOptions(prev => ({ ...prev, [field]: items }));
        })
        .catch(() => {
          if (seq !== requestSeq.current[field]) return;
          clearOptions(field);
        });
    },
    [cacheLabels, clearOptions],
  );

  const loadInitialOptions = useCallback((field: Field) => runQuery('', field), [runQuery]);

  const searchOptions = useCallback(
    (query: string, field: Field) => {
      const trimmed = query.trim();
      if (trimmed.length < MIN_CITY_SEARCH_CHARS) {
        clearOptions(field);
        return;
      }
      runQuery(trimmed, field);
    },
    [runQuery, clearOptions],
  );

  useEffect(() => {
    if (!enabled) return;
    [codes.from, codes.to].forEach(code => {
      if (!code || labels[code]) return;
      getCityById(code)
        .then(city => setLabels(prev => ({ ...prev, [city.id]: city.name })))
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, codes.from, codes.to]);

  return { options, resolveLabel, cacheLabel, clearOptions, loadInitialOptions, searchOptions };
};
